import React, { useState } from 'react';
import {
  SparklesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PencilIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';

// Field mapping component
function FieldMapping({ detected, available, onMappingChange, mapping }) {
  return (
    <div className="space-y-3">
      {detected.map((field) => (
        <div key={field.name} className="flex items-center gap-3">
          <div className="w-1/3">
            <div className="text-sm font-medium text-gray-700">{field.label}</div>
            <div className="text-xs text-gray-500">{field.description}</div>
          </div>
          <div className="text-gray-400">→</div>
          <select
            value={mapping[field.name] || ''}
            onChange={(e) => onMappingChange(field.name, e.target.value)}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">-- Not mapped --</option>
            {available.map(col => (
              <option key={col} value={col}>
                {col} {field.suggested === col && '(suggested)'}
              </option>
            ))}
          </select>
          {mapping[field.name] && (
            <CheckCircleIcon className="h-5 w-5 text-green-500" />
          )}
        </div>
      ))}
    </div>
  );
}

// Visualization suggestion component
function VisualizationSuggestions({ suggestions, onAccept, onModify }) {
  const [expanded, setExpanded] = useState(false);

  if (!suggestions || suggestions.length === 0) return null;

  return (
    <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <SparklesIcon className="h-5 w-5 text-blue-600 mt-0.5" />
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-blue-900">AI-Suggested Visualizations</h4>
          <p className="text-xs text-blue-700 mt-1">
            Based on your data, here are recommended color schemes:
          </p>

          <div className="mt-3 space-y-3">
            {suggestions.slice(0, expanded ? undefined : 2).map((suggestion, idx) => (
              <div key={idx} className="bg-white rounded-lg p-3 border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      Color by: {suggestion.field}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {suggestion.reason}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Preview of colors */}
                    <div className="flex -space-x-1">
                      {Object.values(suggestion.colorScheme || {}).slice(0, 5).map((color, i) => (
                        <div
                          key={i}
                          className="w-4 h-4 rounded-full border-2 border-white"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                    <button
                      onClick={() => onAccept(suggestion)}
                      className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Apply
                    </button>
                    <button
                      onClick={() => onModify(suggestion)}
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {suggestions.length > 2 && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-2"
            >
              {expanded ? (
                <>
                  <ChevronUpIcon className="h-3 w-3" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDownIcon className="h-3 w-3" />
                  Show {suggestions.length - 2} more
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Address fields summary - shows what was auto-detected for geocoding
function AddressFieldsSummary({ mapping }) {
  // These are the address-related field names from the mapping
  const addressFields = ['house', 'houseNumber', 'street', 'city', 'state', 'zip'];
  const detectedAddress = addressFields.filter(f => mapping[f]);

  if (detectedAddress.length === 0) {
    return null;
  }

  return (
    <div className="border border-green-200 bg-green-50 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <MapPinIcon className="h-5 w-5 text-green-600 mt-0.5" />
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-green-900">Address Fields Detected</h4>
          <p className="text-xs text-green-700 mt-1">
            These fields will be used for geocoding to display voters on the map
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {detectedAddress.map(field => (
              <span key={field} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                {field}: {mapping[field]}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SchemaDetection({
  isAnalyzing,
  analysisResult,
  availableColumns,
  onMappingChange,
  mapping,
  onVisualizationAccept,
  onVisualizationModify,
  onReanalyze
}) {
  if (isAnalyzing) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-center gap-3">
          <ArrowPathIcon className="h-6 w-6 text-blue-600 animate-spin" />
          <div>
            <div className="text-sm font-medium text-gray-900">Analyzing your data...</div>
            <div className="text-xs text-gray-500">AI is detecting schema and suggesting visualizations</div>
          </div>
        </div>
      </div>
    );
  }

  if (!analysisResult) {
    return null;
  }

  return (
    <div className="space-y-4">
      {/* Analysis header */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {analysisResult.confidence > 0.8 ? (
              <CheckCircleIcon className="h-6 w-6 text-green-500" />
            ) : (
              <ExclamationTriangleIcon className="h-6 w-6 text-amber-500" />
            )}
            <div>
              <div className="text-sm font-semibold text-gray-900">
                Schema Detection Complete
              </div>
              <div className="text-xs text-gray-500">
                Confidence: {Math.round(analysisResult.confidence * 100)}% |
                {analysisResult.detectedFields?.length || 0} fields detected
              </div>
            </div>
          </div>
          <button
            onClick={onReanalyze}
            className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Re-analyze
          </button>
        </div>

        {/* Data type detection summary */}
        {analysisResult.dataType && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="text-xs text-gray-600">
              <span className="font-medium">Detected data type:</span>{' '}
              {analysisResult.dataType}
            </div>
            {analysisResult.summary && (
              <div className="text-xs text-gray-500 mt-1">
                {analysisResult.summary}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Field mappings */}
      {analysisResult.detectedFields && analysisResult.detectedFields.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Field Mappings</h4>
          <FieldMapping
            detected={analysisResult.detectedFields}
            available={availableColumns}
            mapping={mapping}
            onMappingChange={onMappingChange}
          />
        </div>
      )}

      {/* Address fields summary - shows auto-detected address fields */}
      <AddressFieldsSummary mapping={mapping} />

      {/* Visualization suggestions */}
      <VisualizationSuggestions
        suggestions={analysisResult.visualizationSuggestions}
        onAccept={onVisualizationAccept}
        onModify={onVisualizationModify}
      />
    </div>
  );
}
